import { Injectable, inject } from '@angular/core';
import { debounceTime, filter, map, ReplaySubject, tap, switchMap, of, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import type { HttpErrorReportEvent } from './http-error.models';
import { HttpErrorReporterService } from './http-error-reporter.service';
import { injectRumService } from '../../shared/services/rum/rum.service';
import { isTranslationKey } from './http-error-message.util';

type RequestDescriptor = {
  method: string;
  url: string;
};

// Use a single generic API error translation label for all HTTP statuses.
const GENERIC_HTTP_ERROR_LABEL = 'COMMON.ERRORS.API.GENERIC_ERROR';

type EndpointErrorKey = { method: string; url: string; label: string };

const ENDPOINT_ERROR_KEYS: EndpointErrorKey[] = [
  // This code is commented out because this feature will not be implemented yet.
  // It's here already for the future implementation to avoid merge conflicts and
  // to keep the logic for friendly error messages in place

  // ['*', '/api/slide-scans', 'COMMON.ERRORS.API.SLIDE_SCANS_LOAD'],
  // ['PUT', '/api/slide-scans/verify', 'COMMON.ERRORS.API.SLIDE_SCANS_VERIFY'],
  // ['GET', '/api/filter-info', 'COMMON.ERRORS.API.FILTER_INFO_LOAD'],
];

const getFriendlyHttpErrorLabel = (_status: number, request: RequestDescriptor): string => {
  const normalizedMethod = request.method?.toUpperCase?.() ?? request.method;

  if (typeof request.url !== 'string') { return GENERIC_HTTP_ERROR_LABEL; }

  // Filter endpoints where the configured URL is a prefix of the request URL
  // and the method either matches or is a wildcard ('*' / 'ANY').
  const candidates = ENDPOINT_ERROR_KEYS.filter((e) => {
    const endpointMethod = e.method?.toUpperCase?.() ?? e.method;
    const methodMatches = endpointMethod === '*' || endpointMethod === 'ANY' || endpointMethod === normalizedMethod;
    return methodMatches && request.url.startsWith(e.url);
  });

  if (candidates.length === 0) { return GENERIC_HTTP_ERROR_LABEL; }

  // Prefer the most specific outcome
  candidates.sort((a, b) => b.url.length - a.url.length);
  return candidates[0].label;
};

const getUserFacingErrorLabel = (event: HttpErrorReportEvent): string => {
  const { error, request } = event;

  // Intentionally not surfacing raw backend free-text messages
  // to users. Backend payloads may include technical details (SQL
  // errors, stack traces, etc) which we must not expose in the UI.
  // If in the future we decide to allow backend-provided translation
  // keys (e.g. userMessageKey or uiMessageKey), we can re-enable
  // a safe extraction here that only accepts translation keys.
  //
  // const backendMessage = extractBackendUserFacingMessage(error);
  // if (backendMessage) {
  //   return backendMessage;
  // }

  return getFriendlyHttpErrorLabel(error.status, request);
};
const getFingerprint = (event: HttpErrorReportEvent) => {
  const { error, request } = event;
  return `${request.method}:${request.url}:${error.status}:${error.statusText}`;
};

@Injectable({ providedIn: 'root' })
export class HttpErrorHandlerService {
  private readonly errorSubject = new ReplaySubject<{ message: string; fingerprint: string; route: string }>(1);
  public readonly error$ = this.errorSubject.asObservable();

  private reporter = inject(HttpErrorReporterService);
  private rumService = injectRumService();
  private translateService = inject(TranslateService);
  private readonly clear$ = new Subject<void>();
  private router = inject(Router);

  constructor() {
    this.reporter.errors$.pipe(
      tap((event) => this.logTelemetry(event)),
      filter((event) => !event.suppressed && !event.suppressNotification),

      switchMap((event: HttpErrorReportEvent) => {
        // Capture current route at the moment the error event is received.
        const capturedRoute = this.router.url;

        return of(event).pipe(
          debounceTime(400),
          takeUntil(this.clear$),
          map((ev: HttpErrorReportEvent) => {
            const userFacingError = getUserFacingErrorLabel(ev);
            let message: string;

            if (isTranslationKey(userFacingError)) {
              message = this.translateService.instant(userFacingError);
            } else {
              message = userFacingError;
            }

            return {
              message,
              fingerprint: getFingerprint(ev),
              route: capturedRoute,
            };
          }),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe(({ message, fingerprint, route }) => {
      this.notify(message, fingerprint, route);
    });
    // Do not clear stored errors on every navigation. Instead the emitted
    // error now contains the UI route where it originated and consumers
    // may decide whether to display it depending on the current route.
  }

  private notify(message: string, fingerprint: string, route?: string) {
    this.errorSubject.next({ message, fingerprint, route: route ?? this.router.url });
  }

  private logTelemetry(event: HttpErrorReportEvent) {
    this.rumService.awsRum()?.recordEvent('dpqa.http.error', {
      status: event.error.status,
      statusText: event.error.statusText,
      method: event.request.method,
      url: event.request.url,
      suppressed: event.suppressed,
      suppressNotification: event.suppressNotification,
    });
  }

  public clear(): void {
    this.clear$.next();
    this.errorSubject.next({ message: '', fingerprint: '', route: '' });
  }
}
